import React, { createContext, useState, useEffect } from 'react';
import AssessmentServices from '../api/assessmentServices';

export const AssessmentContext = createContext();

export const AssessmentProvider = ({ children, assessmentId }) => {
  const [assessment, setAssessment] = useState(null);
  const [criterion, setCriterion] = useState({ name: '', answers: [] });
  const [invitation, setInvitation] = useState({ email: '', name: '', status: '', answers: [] });
  const [question, setQuestion] = useState({ name: '', sectionCode: '', criteriaId: '' });
  const [section, setSection] = useState({ name: '', code: '', questions: [] });
  const [loading, setLoading] = useState(true); 

  useEffect(() => {
    const fetchAssessment = async () => {
      setLoading(true);
      try {
        if (!assessmentId) {
          setAssessment({ name: '', description: '', slug: '', criteria: [], sections: [], invitations: [] });
        } else {
          const data = await AssessmentServices.getAssessment(assessmentId);
          data.sections.sort((a, b) => a.order - b.order);
          data.sections.forEach(section => {
            section.questions.sort((a, b) => a.order - b.order);
          });          
          setAssessment(data);
        }
      } catch (error) {
        console.error('Error al cargar los assessments:', error);
      } finally {
        setLoading(false); 
      }
    };

    fetchAssessment();
  }, [assessmentId]);

  const deleteCriterion = async (criterionId) => {
    try {
      await AssessmentServices.deleteCriterion(assessmentId, criterionId);
      setAssessment((prevAssessment) => ({
        ...prevAssessment,
        criteria: prevAssessment.criteria.filter(c => c.id !== criterionId)
      }));
      setCriterion(null);
    } catch (error) {
      console.error('Error al eliminar el criterio:', error);
    }
  }

  const deleteQuestion = async (questionId) => {
    try {
      await AssessmentServices.deleteQuestion(assessmentId, questionId);
      setAssessment((prevAssessment) => ({
        ...prevAssessment,
        sections: prevAssessment.sections.map(section => ({
          ...section,
          questions: section.questions.filter(q => q.id !== questionId)
        }))
      }));
    } catch (error) {
      console.error('Error al eliminar la pregunta:', error);
    }
  }

  const deleteSection = async (sectionId) => {
    try {
      await AssessmentServices.deleteSection(assessmentId, sectionId);
      setAssessment((prevAssessment) => ({
        ...prevAssessment,
        sections: prevAssessment.sections.filter(s => s.id !== sectionId)
      }));
    } catch (error) {
      console.error('Error al eliminar la sección:', error);
    }
  }

  const saveAssessment = async (assessmentData) => {
    try {
      if (assessmentId) assessmentData = { ...assessmentData, id: assessmentId };
      const data = await AssessmentServices.saveAssessment(assessmentData);
      if (assessment && assessment.criteria) {
        data.attributes.criteria = assessment.criteria;
      }
      if (assessment && assessment.sections) {
        data.attributes.sections = assessment.sections;
      }
      data.attributes.sections.sort((a, b) => a.order - b.order);
      data.attributes.sections.forEach(section => {
        section.questions.sort((a, b) => a.order - b.order);
      });
      setAssessment(data.attributes);
      return data.attributes;
    } catch (error) {
      console.error('Error al guardar el assessment:', error);
    }
  }

  const saveCriterion = async (criterionData) => {
    try {
      const data = await AssessmentServices.saveCriterion(assessmentId, criterionData);
      setCriterion(data.attributes);
      setAssessment((prevAssessment) => {
        const index = prevAssessment.criteria.findIndex(c => c.id === criterion.id);
        if (index !== -1) {
          const updatedCriteria = [...prevAssessment.criteria];
          updatedCriteria[index] = data.attributes;
          return { ...prevAssessment, criteria: updatedCriteria };
        } else {
          return { ...prevAssessment, criteria: [...prevAssessment.criteria, data.attributes] };
        }
      });
    } catch (error) {
      console.error('Error al agregar el criterio:', error);
    }
  }

  const saveInvitation = async (invitation) => {
    try {
      invitation.assessmentId = assessmentId;
      const data = await AssessmentServices.saveInvitation(assessmentId, invitation);
      setAssessment((prevAssessment) => {
        return {
          ...prevAssessment,
          invitations: [...prevAssessment.invitations, data.attributes]
        };
      });
    } catch (error) {
      console.error('Error al agregar la invitación:', error);
    }
  }

  const saveQuestion = async (question) => {
    try {
      const data = await AssessmentServices.saveQuestion(assessmentId, question);
      setAssessment((prevAssessment) => {
        return {
          ...prevAssessment,
          sections: prevAssessment.sections.map(section => {
            if (section.code === question.sectionCode) {
              const questionIndex = section.questions.findIndex(q => q.id === question.id);
              if (questionIndex !== -1) {
                section.questions[questionIndex] = data.attributes;
              } else {
                section.questions.push(data.attributes);
              }
            }
            return section;
          })
        };
      });
    } catch (error) {
      console.error('Error al agregar la pregunta:', error);
    }
  }

  const saveSection = async (section) => {
    try {
      const data = await AssessmentServices.saveSection(assessmentId, section);
      setAssessment((prevAssessment) => {
        const index = prevAssessment.sections.findIndex(s => s.id === section.id);
        if (index !== -1) {
          const updatedSections = [...prevAssessment.sections];
          if (!data.attributes.questions) {
            data.attributes.questions = updatedSections[index].questions;
          }
          updatedSections[index] = data.attributes;
          return { ...prevAssessment, sections: updatedSections };
        } else {
          return { ...prevAssessment, sections: [...prevAssessment.sections, data.attributes] };
        }
      });
    } catch (error) {
      console.error('Error al agregar la sección:', error);
    }
  }

  const selectCriterion = (criterionId) => {
    const criterion = assessment.criteria.find(c => c.id === criterionId);
    if (!criterion) {
      setCriterion({ name: '', answers: [] });
      return;
    } else {
      setCriterion(criterion);
    }
  }

  const selectInvitation = (invitationId) => {
    const invitation = assessment.invitations.find(i => i.id === invitationId);
    if (!invitation) {
      setInvitation({ email: '', name: '', status: '', answers: [] });
      return;
    } else {
      setInvitation(invitation);
    }
  }

  const selectQuestion = (questionId) => {
    const q = assessment.sections
      .flatMap(section => section.questions)
      .find(q => q.id === questionId);
    if (!q) {
      setQuestion({ name: '', sectionCode: '', criteriaId: '' });
      return;
    } else {
      setQuestion(q);
    }
  }

  const selectSection = (sectionId) => {
    const section = assessment.sections.find(c => c.id === sectionId);
    if (!section) {
      setSection({ name: '', code: '' });
      return;
    } else {
      setSection(section);
    }
  }

  return (
    <AssessmentContext.Provider value={{ 
      assessment, 
      loading,
      criterion,
      invitation,
      question,
      section,
      deleteCriterion,
      deleteQuestion,
      deleteSection,
      saveAssessment,
      saveCriterion,
      saveInvitation,
      saveQuestion,
      saveSection,
      selectCriterion,
      selectInvitation,
      selectQuestion,
      selectSection
    }}>
      {children}
    </AssessmentContext.Provider>
  );
};
